import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { NOTIFICATION_TIME_OUT } from "../../../../constants/app";
import { useReopenedStages } from "../../../../hooks/useReopenedStages";
import { DOCUMENT_CONFIGS } from "../../../../models/superadmin";
import { formatAllowance, formatDate } from "../../../../models/vacancy";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { CandidateStorage } from "../../../../services/CandidateStorage";
import ImpersonationService from "../../../../services/ImpersonationService";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import { showNotificationAction } from "../../../../store/application/slice";
import { TUploadedDocumentsData } from "../../../../store/onboarding/types";
import { resetReferooDocumentsAction, setJobAdderIdPopupShowAction } from "../../../../store/superadmin/slice";
import {
  activateCandidateBySuperAdmin,
  makeCandidateInactive,
  reopenStageThunk
} from "../../../../store/superadmin/thunks";
import {
  ICandidateDetails,
  ICandidateReviewFormInput,
  ICompletionStatus,
  IProfile,
} from "../../../../store/superadmin/types";
import { TFileUploadsResponse } from "../../../../store/uploads/types";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import { isDateExpired } from "../../../../utils/documentHelper";
import { getLatestStage2Action } from "../../../../utils/stage2ActionHelper";
import { isStageReopened } from "../../../../utils/stageHelper";
import { extractSuburbFromAddress } from "../../../../utils/stringUtils";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Alert from "../../../UI/Icons/Alert";
import ByPass from "../../../UI/Icons/ByPass";
import CheckOutline from "../../../UI/Icons/CheckOutline";
import EditPencil from "../../../UI/Icons/EditPencil";
import ExternalLink from "../../../UI/Icons/ExternalLink";
import InactiveOutline from "../../../UI/Icons/InactiveOutline";
import PlaceHolderImage from "../../../UI/Icons/PlaceHolderImage";
import Reopen from "../../../UI/Icons/Reopen";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Warning from "../../../UI/Icons/Warning";
import RadioInput from "../../../UI/Inputs/RadioInput";
import PopupModal from "../../../UI/Popup";
import StarRating from "../../../UI/StarRating";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";
import AdminComments from "../AdminComments";
import CandidateJobs from "../CandidateJobs";
import LoadAllowance from "../LoadAllowance";
import ReopenStageModal from "../ReopenStageModal/Index";
import StageDisplay from "../StageDisplay/Index";
import { getStageInfo as getStageInfoHelper } from "./candidateReviewHelpers";
import ComplianceCheck from "./ComplianceCheck";
import DocumentAndContract from "./DocumentAndContract";
import IdVerification from "./IdVerification";
import JobPreference from "./JobPreference";
import NextOfKin from "./NextOfKin";
import OnlineInterview from "./OnlineInterview";
import PersonalDetails from "./PersonalDetails";
import ProfileEditRequest from "./ProfileEditRequest";
import Qualification from "./Qualification";

const steps: {
  name: string;
  stage: number;
  section_name: string;
  key: keyof ICompletionStatus | string;
}[] = [
  {
    name: "ID Verification",
    stage: 1,
    section_name: "idverification",
    key: "id_verification",
  },
  {
    name: "Personal Details",
    stage: 1,
    section_name: "personalDetails",
    key: "personal_details",
  },
  {
    name: "Qualifications",
    stage: 1,
    section_name: "qualifications",
    key: "qualification",
  },
  {
    name: "Job Preferences",
    stage: 1,
    section_name: "jobpreference",
    key: "jobpreference",
  },
  {
    name: "Online Interview",
    stage: 1,
    section_name: "onlineInterview",
    key: "online_interview",
  },
  {
    name: "Compliance Check",
    stage: 2,
    section_name: "complianceCheck",
    key: "compliance_check",
  },
  {
    name: "Documents & Contract",
    stage: 3,
    section_name: "documents",
    key: "documents",
  },
  {
    name: "Next of Kin",
    stage: 4,
    section_name: "emergencyInfo",
    key: "emergency_info",
  },
  {
    name: "Bank Account",
    stage: 5,
    section_name: "bankAccount",
    key: "bankAccount",
  },
];

/**
 * Props for the CandidateReviewModal component.
 */
interface CandidateReviewModalProps {
  /**
   * Determines whether the modal is visible or not.
   */
  showModal: boolean;

  /**
   * Function to update the visibility state of the modal.
   * @param showModal - A boolean indicating whether to show or hide the modal.
   */
  setShowModal: (showModal: boolean) => void;

  /**
   * The ID of the currently selected candidate.
   */
  currentCandidateId: number;

  /**
   * Data of the candidate being reviewed. Can be null if no candidate is selected.
   */
  reviewCandidateData: ICandidateDetails | null;

  /**
   * Function to fetch and refresh the list of all candidates.
   */
  getAllCandidates?: () => void;
  /**
   * Function to handle to get review data of a candidate.
   * @param candidateId
   * @returns
   */
  handleReviewCandidate?: (
    candidateId: number,
    candidateUUID: string,
    currentCandidatePrimaryId: number
  ) => void;
  /**
   * The UUID of the current candidate being reviewed.
   *
   */
  currentCandidateUUID: string;

  currentCandidatePrimaryId: number;

  getCandidateQualification?: (
    value: string,
    alternativePositions: string
  ) => string;
}

/**
 * CandidateReviewModal is a React functional component that renders a modal for reviewing
 * candidate details and managing their progression through various stages of the recruitment process.
 *
 * @component
 * @param {CandidateReviewModalProps} props - The props for the component.
 * @param {boolean} [props.showModal=false] - Determines whether the modal is visible.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setShowModal - Function to toggle the modal visibility.
 * @param {number} props.currentCandidateId - The ID of the candidate being reviewed.
 * @param {ICandidateDetails} props.reviewCandidateData - The data of the candidate being reviewed, including profile details, qualifications, and documents.
 *
 * @returns {JSX.Element} The rendered CandidateReviewModal component.
 *
 * @description
 * This component provides a detailed interface for reviewing a candidate's profile, including:
 * - Personal details
 * - Qualifications
 * - Job preferences
 * - Online interview status
 * - Compliance checks
 * - Signed documents and contracts
 *
 * It allows the admin to navigate through different sections of the candidate's profile,
 * approve or decline the candidate's progression, and download relevant documents.
 *
 * The component also includes functionality for bypassing certain requirements (e.g., online interview, compliance check)
 * and handles form submission for candidate activation or progression.
 *
 */
const CandidateReviewModal: React.FC<CandidateReviewModalProps> = ({
  showModal = false,
  setShowModal,
  currentCandidateId,
  reviewCandidateData,
  getAllCandidates,
  handleReviewCandidate,
  currentCandidateUUID,
  currentCandidatePrimaryId,
  getCandidateQualification,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const toTitleCase = (str: string): string =>
    str
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const inProgress = useSelector((state) => state.superadmin.inProgress);
  const isShowNotification = useSelector((state) => state.app.isShowNotification);
  const popUpDetails = useSelector((state) => state.superadmin.showJobAdderPopup);
  const stepCompletion = reviewCandidateData?.completion_status;
  const reopenedStagesStatus = useSelector((state) => state.superadmin.reopenedStagesStatus);
  const isReferooDocumentFound = useSelector((state) => state.superadmin.referooDocumentsFound);
  const referooPoliceCheckDoc = useSelector((state) => state.superadmin.policeCheckDoc);
  const referooRightToWorkDoc = useSelector((state) => state.superadmin.referooRightToWorkDoc);
  const referooRightToWorkMainDoc = useSelector((state) => state.superadmin.rightToWorkDoc);
  const referooReferenceCheckDoc = useSelector((state) => state.superadmin.referooReferenceCheckDoc);

  const [showOnlineInterview, setShowOnlineInterview] =
    useState<boolean>(false);
  const [showComplianceCheck, setShowComplianceCheck] =
    useState<boolean>(false);
  const [showDocuments, setShowDocuments] = useState<boolean>(false);
  const [showPersonalDetails, setShowPersonalDetails] =
    useState<boolean>(false);
  const [showIdVerification, setShowIdVerification] = useState<boolean>(false);
  const [showJobPreference, setShowJobPreference] = useState<boolean>(false);
  const [showQualifications, setShowQualifications] = useState<boolean>(false);
  const [skipOnlineInterview, setSkipOnlineInterview] = useState<number>(0);
  const [skipIdVerification, setSkipIdVerification] = useState<number>(0);
  const [skipComplianceCheck, setSkipComplianceCheck] = useState<number>(0);
  const [reviewActionValue, setReviewActionValue] = useState<string>("");
  const [showAdminComments, setShowAdminComments] = useState<boolean>(false);
  const [showCandidateJobs, setShowCandidateJobs] = useState<boolean>(false);
  const [showEmergencyInfo, setShowEmergencyInfo] = useState<boolean>(false);
  const [showAllowanceLoad, setShowAllowanceLoad] = useState<boolean>(false);
  const [typeofCandidateJobs, setTypeofCandidateJobs] = useState<string>("")
  const [isAdminApproveReferoo, setIsAdminApproveReferoo] =
    useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isValidDocAndContracts, setIsValidDocAndContracts] =
    useState<boolean>(false);
  const [totalAdminComments, setTotalAdminComments] = useState<number>(
    reviewCandidateData?.comments_count || 0
  );
  const [missingDocs, setMissingDocs] = useState<string[]>([]);
  const [pendingSteps, setPendingSteps] = useState<string>('');
  const [selectRadioAuto, setSelectRadioAuto] = useState<boolean>(false);
  const [isAccessingOnboarding, setIsAccessingOnboarding] = useState<boolean>(false);
  const [showReopenStageModal, setShowReopenStageModal] = useState<boolean>(false);
  const [reopenStageNumber, setReopenStageNumber] = useState<number>(1);
  const [reopenStageError, setReopenStageError] = useState<string | null>(null);

  const currentStage =
    reviewCandidateData &&
    reviewCandidateData?.profile?.profile_steps?.length > 0
      ? reviewCandidateData?.profile?.profile_steps[0]?.step_number + 1
      : 1;

  const tfnFormUrl =
    reviewCandidateData?.flk_documents?.find(
      (item) => item.is_active_tfn_form === "1"
    )?.s3_pdf_url ?? null;
  const superFormUrl =
    reviewCandidateData?.flk_documents?.find(
      (item) => item.is_active_super_form === "1"
    )?.s3_pdf_url ?? null;
  const contractForm =
    reviewCandidateData?.flk_documents?.find(
      (item) => item.is_active_contract_form === "1"
    )?.s3_pdf_url ?? null;
  const profileCompletionStatus: ICompletionStatus | undefined =
    reviewCandidateData?.completion_status;
  const lastAction = reviewCandidateData?.profile?.profile_steps?.[0]?.action;
  const viddiInterviewStatus =
    reviewCandidateData?.viddi_status?.viddi_candidate_interview_status;
  const referroStatus = reviewCandidateData?.referoo_status;
  const expiringDocumentsCollections = reviewCandidateData?.documents_expire_one_month_documents ?? [];

  const qualificationTypes = DOCUMENT_CONFIGS.map((config) => config.type);
  const complianceTypes = ["police_check", "working_with_children_check"];

  const { isSectionReopenedFn, reopenedSections } = useReopenedStages();
  const isAdminCompleteReopened = isSectionReopenedFn('admin_complete');

  const expiredQualificationDocs: string[] = [];
  const expiredComplianceDocs: string[] = [];

  const workingRights = reviewCandidateData?.profile?.working_rights;

  expiringDocumentsCollections.forEach((doc) => {
    const { document_type } = doc;

    // Skip australian_work_visa if working_rights is australian_permanent_resident
    if (document_type === "australian_work_visa" && workingRights === "australian_permanent_resident") {
      return;
    }

    // Skip permanent_residency_visa if working_rights is australian_visa_holder
    if (document_type === "permanent_residency_visa" && workingRights === "australian_visa_holder") {
      return;
    }

    if (qualificationTypes.includes(document_type)) {
      const match = DOCUMENT_CONFIGS.find((config) => config.type === document_type);
      if (match) expiredQualificationDocs.push(match.title);
    }

    if (complianceTypes.includes(document_type)) {
      const label = document_type === "police_check"
        ? "Police Check"
        : "Working With Children Check / Blue Card";
      expiredComplianceDocs.push(label);
    }
  });

  const isPersonalDetailsHaveResume = useMemo(() => {
    if(reviewCandidateData?.profile.resume === null || reviewCandidateData?.profile === null) {
      return true;
    }
    return false;
  }, [reviewCandidateData]);

  const latestStage2Action = getLatestStage2Action(reviewCandidateData?.profile?.profile_steps ?? [], reviewCandidateData?.reopened_stages_status, reviewCandidateData?.referoo_candidate?.created_at);

  // useEffect(() => {
  //   if(!isShowNotification && !popUpDetails?.showPopupModal && getAllCandidates) {
  //     dispatch(getAllCandidates)
  //   }
  // }, [dispatch, isShowNotification, popUpDetails])

  useEffect(() => {
    const handleCheckMandatoryQualificationDocs = () => {
      const requiredDocs: { condition: boolean; type: string }[] = [
        { condition: reviewCandidateData?.qualifications?.acecqa_approved ? true : false, type: "ACECQA_assessment" },
        { condition: reviewCandidateData?.qualifications?.alternative_positions?.includes("Chef/Cook") ?? false, type: "food_handler_certificate" },
        { condition: reviewCandidateData?.qualifications?.alternative_positions?.includes("Chef/Cook") ?? false, type: "cooking_qualification" },
        { condition: (reviewCandidateData?.qualifications?.alternative_positions?.includes("Chef/Cook") || reviewCandidateData?.qualifications?.alternative_positions?.includes("Cleaner") || reviewCandidateData?.qualifications?.alternative_positions?.includes("Gardener") || reviewCandidateData?.qualifications?.alternative_positions?.includes("Admin Assistant")) ?? false, type: "mandatory_reporting_training" },
        { condition: (reviewCandidateData?.qualifications?.alternative_positions?.includes("Cleaner") || reviewCandidateData?.qualifications?.alternative_positions?.includes("Gardener") || reviewCandidateData?.qualifications?.alternative_positions?.includes("Admin Assistant")) ?? false, type: "working_with_children_check" },
        { condition: reviewCandidateData?.qualifications?.qualification?.includes("post_graduate_diploma") ?? false, type: "post_graduate_diploma" },
        { condition: reviewCandidateData?.qualifications?.qualification?.includes("bachelor_or_degree") ?? false, type: "bachelor_or_degree" },
        { condition: ["post_graduate_diploma", "bachelor_or_degree"].includes(reviewCandidateData?.qualifications?.qualification ?? ""), type: "teachers_registration" },
        { condition: reviewCandidateData?.qualifications?.qualification === "certificate_iii", type: "certificate_iii" },
        { condition: (reviewCandidateData?.profile !== null) && reviewCandidateData?.profile?.working_rights === "australian_permanent_resident", type: "permanent_residency_visa" },
        { condition: (reviewCandidateData?.profile !== null) && reviewCandidateData?.profile?.working_rights === "australian_visa_holder", type: "australian_work_visa" }
      ];

      const missing: string[] = [];

      for (const { condition, type } of requiredDocs) {
        const doc = reviewCandidateData?.documents?.find((d) => d.document_type === type);
        if (condition && (!doc || doc.file_path === "")) {
          missing.push(type);
        }
      }

      setMissingDocs(missing);
      return missing;
    };

    


    const handleCheckMandatoryPersonalDetailsDocs = () => {
      const isMissing = reviewCandidateData?.profile?.resume === null || reviewCandidateData?.profile === null;
      return isMissing;
    };

    const missingPersonal = handleCheckMandatoryPersonalDetailsDocs();
    const missingQualification = handleCheckMandatoryQualificationDocs();

    const steps: string[] = [];

    if (missingPersonal) steps.push("Personal Details");
    if (missingQualification.length > 0) steps.push("Qualifications");

    const pendingStepsText = steps.join(", ");

    setPendingSteps(pendingStepsText);
  }, [reviewCandidateData]);

  // Sync local state with API data on mount/update
  useEffect(() => { 
    if (reviewCandidateData?.profile?.bypass_compliance_check !== undefined) {
      setSkipComplianceCheck(reviewCandidateData.profile.bypass_compliance_check);
      setFieldValue("bypass_compliance_check", reviewCandidateData.profile.bypass_compliance_check);
    }
    if (reviewCandidateData?.profile?.bypass_online_interview !== undefined) {
      setSkipOnlineInterview(reviewCandidateData.profile.bypass_online_interview);
      setFieldValue("bypass_online_interview", reviewCandidateData.profile.bypass_online_interview);
    }
    if (reviewCandidateData?.profile?.bypass_id_verification !== undefined) {
      setSkipIdVerification(reviewCandidateData.profile.bypass_id_verification);
      setFieldValue("bypass_id_verification", reviewCandidateData.profile.bypass_id_verification);
    }
  }, [reviewCandidateData]);

  /**
   * Handles the close action for the modal.
   *
   * This function performs the following actions:
   * 1. Closes the modal by setting the `showModal` state to `false`.
   * 2. Refreshes the list of candidates by invoking the `getAllCandidates` function.
   */
  const onClickClose = () => {
    dispatch(resetReferooDocumentsAction()); // Reset referoo documents on error
    setShowModal(false);
    getAllCandidates?.(); // Optional chaining to safely call if defined
    setFieldValue("action", "");
    document.body.classList.remove("modal-open");
  };

  /**
   * Conver the ISO date string to a readable format DD-MM-YYY.
   * @param isoDateString - The ISO date string to format.
   * @returns
   */
  const formatDateToReadable = (isoDateString: string): string => {
    if (!isoDateString) return ""; // Invalid date
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) return ""; // Invalid Date
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    let finalDate = new Intl.DateTimeFormat("en-GB", options).format(date);
    finalDate = new Intl.DateTimeFormat("en-GB", options)
          .format(date)
          .replace(",", ""); // Remove comma
    return finalDate;
  };

  /**
   * Convert a snake_case string to Title Case.
   * @param snakeCaseString - The snake_case string to convert.
   * @returns The converted Title Case string.
   */
  const convertToTitleCase = (
    snakeCaseString: string | null | undefined
  ): string => {
    if (!snakeCaseString || snakeCaseString.trim() === "") {
      return "";
    }
    return snakeCaseString
      .split("_") // Split the string by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
      .join(" "); // Join the words with spaces
  };

  /**
   * Handle for section navigation based on the selected section.
   * @param section - The section to navigate to.
   */
  const handleSectionNavigation = (section: string) => {
    setShowPersonalDetails(section === "personalDetails");
    setShowOnlineInterview(section === "onlineInterview");
    setShowComplianceCheck(section === "complianceCheck");
    setShowJobPreference(section === "jobpreference");
    setShowQualifications(section === "qualifications");
    setShowDocuments(section === "documents");
    setShowIdVerification(section === "idverification");
    setShowEmergencyInfo(section === "emergencyInfo");
  };

  const getSectionClass = (section: string): string => {
    if (
      (section === "personalDetails" && showPersonalDetails) ||
      (section === "onlineInterview" && showOnlineInterview) ||
      (section === "complianceCheck" && showComplianceCheck) ||
      (section === "jobpreference" && showJobPreference) ||
      (section === "qualifications" && showQualifications) ||
      (section === "documents" && showDocuments) ||
      (section === "idverification" && showIdVerification) ||
      (section === "emergencyInfo" && showEmergencyInfo)
    ) {
      return "fw-700";
    }
    return "";
  };

  const isStepBypassed = (step: {
    section_name: string;
    key: keyof ICompletionStatus | string;
    stage: number;
  }): boolean => {
    const bypassFlags: Record<string, keyof ICandidateReviewFormInput> = {
      idverification: "bypass_id_verification",
      onlineInterview: "bypass_online_interview",
      complianceCheck: "bypass_compliance_check",
    };

    const keyFromValues = bypassFlags[step.section_name];
    const bypassFromValues = keyFromValues 
      ? values[keyFromValues] === 1
      : false;

    const bypassFromProfile =
      reviewCandidateData?.profile?.[`bypass_${step.key}` as keyof IProfile] ===
      1;

    // Special handling for compliance check
    if (step.section_name === "complianceCheck") {

      if ((bypassFromValues || bypassFromProfile)) {
        return true;
      }

      // If bypass is checked, always show ByPass icon regardless of documents
      if ((bypassFromValues || bypassFromProfile) && reviewCandidateData?.referoo_status !== "complete") {
        return true;
      } 
      
      // If documents are expired or referoo not complete, can't show as bypassed/completed
      if (referroStatus !== "complete" || expiredComplianceDocs.length > 0) {
        return false;
      }

      return referroStatus !== "complete";
    }

    return bypassFromValues || bypassFromProfile;
  };

  const isStepErrored = (step: {
    section_name: string;
    key: keyof ICompletionStatus | string;
    stage: number;
  }): boolean => {
    const isIncomplete =
      profileCompletionStatus?.[step.key as keyof ICompletionStatus] === 0;

    const idVerificationError =
      profileCompletionStatus?.["id_verification"] === 0 &&
      step.section_name === "idverification" &&
      step.stage === 1 &&
      values?.bypass_id_verification === 0;

    const personalDetailsError =
      profileCompletionStatus?.["personal_details"] === 1 &&
      step.section_name === "personalDetails" &&
      step.stage === 1 &&
      isPersonalDetailsHaveResume;

    const qualificationError =
      profileCompletionStatus?.["qualification"] === 1 &&
      step.section_name === "qualifications" &&
      step.stage === 1 &&
      (missingDocs.length > 0 || expiredQualificationDocs.length > 0 || reviewCandidateData?.documents?.length === 0);

    const onlineInterviewError =
      step.section_name === "onlineInterview" &&
      step.stage === 1 &&
      values?.bypass_online_interview === 0 &&
      viddiInterviewStatus === 0;

    const complianceCheckError =
      step.section_name === "complianceCheck" &&
      step.stage === 2 &&
      isComplianceCheckErrored;

    const bankAccountError = 
      step.section_name === "bankAccount" &&
      step.stage === 5 &&
      reviewCandidateData?.completion_status.bankAccount === 0

    const documentError =
      step.section_name === "documents" &&
      step.stage === 3 &&
      !isValidDocAndContracts;

    return (
      isIncomplete ||
      idVerificationError ||
      personalDetailsError || 
      qualificationError || 
      onlineInterviewError ||
      complianceCheckError ||
      bankAccountError || 
      documentError
    );
  };

  const visibleSteps = useMemo(() => {
    return steps.filter((step) => step.stage <= currentStage);
  }, [currentStage]);

  /**
   * * Generates the steps for the candidate review process based on the current stage.
   * @param currentStage - The current stage of the candidate's profile.
   * @returns
   */
  const generateSteps = (currentStage: number) => {
    return visibleSteps.map((step) => {
      const showByPass = isStepBypassed(step);
      const showError = isStepErrored(step);
      const isReopened = reviewCandidateData ? isStageReopened(reviewCandidateData, step.stage) : false;
      const icon = isReopened ?
        <Reopen /> 
        : showByPass ? (
        <ByPass />
      ) : showError ? (
        <Alert />
      )  : (
        <CheckOutline />
      );

      return (
        <div className="d-flex gap-10 align-items-center" key={step.name}>
          {icon}
          <div
            className={`fs-16 cursor-pointer ${getSectionClass(
              step.section_name
            )}`}
            onClick={() => handleSectionNavigation(step.section_name)}
          >
            {step.name}
          </div>
        </div>
      );
    });
  };
  
  const handleJobAdderPopUpModal = async () => {
    await dispatch(setJobAdderIdPopupShowAction({
      showPopupModal: true,
      candidateUuid: currentCandidateUUID
    }));
    document.body.classList.remove("modal-open");
  };

  useEffect(() => {
    generateSteps(currentStage);
  }, [
    showPersonalDetails,
    showOnlineInterview,
    showComplianceCheck,
    showJobPreference,
    showDocuments,
  ]);

  /**
   * Handles the click event for candidate activation
   */
  const {
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setFieldError,
  } = useFormik<ICandidateReviewFormInput>({
    validationSchema: createValidationSchema("activate_profile_super_admin"),
    initialValues: {
      candidate_id: currentCandidatePrimaryId,
      step: 1,
      action: "",
      bypass_compliance_check:
        reviewCandidateData?.profile?.bypass_compliance_check ?? 0,
      bypass_online_interview:
        reviewCandidateData?.profile?.bypass_online_interview ?? 0,
      bypass_id_verification:
        reviewCandidateData?.profile?.bypass_id_verification ?? 0,
      id_verification_bypass_reason:
        reviewCandidateData?.profile?.step_bypass_reason?.find(
          (type) => type.bypass_type === 'bypass_id_verification'
        )?.reason ?? null,
      online_interview_bypass_reason: 
        reviewCandidateData?.profile?.step_bypass_reason?.find(
          (type) => type.bypass_type === 'bypass_online_interview'
        )?.reason ?? null,
      complaince_check_bypass_reason: 
        reviewCandidateData?.profile?.step_bypass_reason?.find(
          (type) => type.bypass_type === 'bypass_compliance_check'
        )?.reason ?? null,
    },
    onSubmit: async (values: ICandidateReviewFormInput) => {
      const formData = new FormData();
      formData.append("candidate_id", currentCandidatePrimaryId.toString());
      formData.append("step", currentStage.toString());
      formData.append("action", reviewActionValue);
      formData.append(
        "bypass_compliance_check",
        skipComplianceCheck.toString()
      );
      formData.append(
        "bypass_online_interview",
        skipOnlineInterview.toString()
      );
      formData.append("bypass_id_verification", skipIdVerification.toString());
      formData.append("id_verification_bypass_reason", values.id_verification_bypass_reason ?? "");
      formData.append("online_interview_bypass_reason", values.online_interview_bypass_reason ?? "");
      formData.append("complaince_check_bypass_reason", values.complaince_check_bypass_reason ?? "");

      try {
        const activateResponse = await dispatch(
          activateCandidateBySuperAdmin({
            FormData: formData,
            currentCandidateId,
            currentCandidateUUID,
          })
        );
        if (isFulfilled(activateResponse)) {
          if (values.action === "decline") {
            dispatch(showNotificationAction("Declined"));
            setShowModal(false);
            setTimeout(() => {
              // getAllCandidates?.(); // Optional chaining to safely call if defined
              document.body.classList.remove("modal-open");
            }, NOTIFICATION_TIME_OUT);
            document.body.classList.remove("modal-open");
          } else {
            let nextStage = currentStage + 1;
            if (nextStage === 2) {
              handleSectionNavigation("complianceCheck");
            } else if (nextStage === 3) {
              handleSectionNavigation("documents");
            }
            setFieldValue("action", "");
            setFieldError("action", "");
            setFieldTouched("action", false);
          }

          if (values.action === "activate") {
            setShowModal(false);
            dispatch(showNotificationAction("Activated"));
            setTimeout(() => {
              // getAllCandidates?.(); // Optional chaining to safely call if defined
              document.body.classList.remove("modal-open");

              // handleJobAdderPopUpModal() // commented this feature, not need
            }, NOTIFICATION_TIME_OUT);
          }

          if (
            (currentStage === 1 || currentStage === 2) &&
            values.action !== "decline"
          ) {
            dispatch(showNotificationAction("Approved"));
            setShowModal(false);
            setTimeout(() => {
              if(values.action === "approve_candidate_bypass_compliance_check") {
                setShowModal(true);
              }else{
                // getAllCandidates?.(); // Optional chaining to safely call if defined
                document.body.classList.remove("modal-open");
              }
              
            }, NOTIFICATION_TIME_OUT);
          }
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  useEffect(() => {
    if (
      (currentStage === 3 && reviewActionValue !== "decline") &&  
      (tfnFormUrl === null ||
      superFormUrl === null ||
      contractForm === null || 
      stepCompletion?.bankAccount !== 1 || 
      stepCompletion?.emergency_info !== 1)
    ) {
      isAdminCompleteReopened === false && setIsDisabled(true);
      setIsValidDocAndContracts(false);
    } else if (
      reviewActionValue !== "decline" &&
      reviewCandidateData?.profile?.current_stage === 1 &&
      reviewCandidateData?.profile?.review_status === "waiting" &&
      reviewCandidateData?.completion_status.id_verification === 0 &&
      reviewCandidateData?.completion_status.online_interview === 0
    ) {
      const hasIdReason = !!values?.id_verification_bypass_reason?.trim();
      const hasInterviewReason = !!values?.online_interview_bypass_reason?.trim();

      if (hasIdReason && hasInterviewReason &&
          values?.bypass_online_interview === 1 && 
          values?.bypass_id_verification === 1
      ) {
        setIsDisabled(false);
        setSelectRadioAuto(true);
      } else {
        setIsDisabled(true);
      }
    } else if(
      reviewActionValue !== "decline" &&
      reviewCandidateData?.profile?.current_stage === 1 &&
      reviewCandidateData?.profile?.review_status === "waiting" &&
      reviewCandidateData?.completion_status.id_verification === 0
    ) {
      const hasIdReason = !!values?.id_verification_bypass_reason?.trim();

      if (hasIdReason && values?.bypass_id_verification === 1) {
        setIsDisabled(false);
        setSelectRadioAuto(true);
      } else {
        setIsDisabled(true);
      }
    } else if(
      reviewActionValue !== "decline" &&
      reviewCandidateData?.profile?.current_stage === 1 &&
      reviewCandidateData?.profile?.review_status === "waiting" &&
      reviewCandidateData?.completion_status.online_interview === 0
    ) {
      const hasInterviewReason = !!values?.online_interview_bypass_reason?.trim();

      if (hasInterviewReason && values?.bypass_online_interview === 1) {
        setIsDisabled(false);
        setSelectRadioAuto(true);
      } else {
        setIsDisabled(true);
      }
    } else if (
      reviewActionValue !== "decline" &&
      reviewCandidateData?.profile?.current_stage === 1 &&
      reviewCandidateData?.profile?.review_status === "approved" && 
      referroStatus !== "complete"
    ) {
      setIsDisabled(!complianceDocsReady);
    } else if(
       reviewActionValue !== "decline" &&
       reviewCandidateData?.profile?.current_stage === 2
    ) {
      if(latestStage2Action === "referoo") {
         referroStatus === "complete" ? setIsDisabled(false) : setIsDisabled(true);
      } else if(latestStage2Action === "admin-complete") {
        setIsDisabled(isComplianceCheckErrored);


      }
    } else {
      setIsDisabled(false); 
      setIsValidDocAndContracts(true);
    }
  }, [currentStage, tfnFormUrl, superFormUrl, contractForm, reviewActionValue, stepCompletion, pendingSteps, values, reviewCandidateData]);


  useEffect(() => {
    if(selectRadioAuto) {
      // Automatically set values when needed
      setReviewActionValue("approve_candidate_to_stage_2");
      setFieldValue("action", "approve_candidate_to_stage_2"); 
    }
  }, [selectRadioAuto, setFieldValue, setReviewActionValue]);
  
  useEffect(() => {
    if (errors?.bypass_compliance_check !== "") {
      // handleSectionNavigation("personalDetails");
    }
  }, [errors?.bypass_compliance_check]);

  useEffect(() => {
    if (currentStage === 1) {
      handleSectionNavigation("idverification");
    } else if (currentStage === 2) {
      handleSectionNavigation("complianceCheck");
    } else if (currentStage === 3) {
      handleSectionNavigation("documents");
    } else {
      handleSectionNavigation("idverification");
    }
  }, []);



  /**
   * * Renders a radio input component with the specified properties.
   * @param checkedValue
   * @param label
   * @param actionValue
   * @param setFieldValue
   * @param setReviewActionValue
   * @returns
   */
  const renderRadioInput = (
    checkedValue: string,
    label: string,
    actionValue: string,
    setFieldValue: (field: string, value: string | number) => void,
    setReviewActionValue: (value: string) => void
  ) => {
    return (
      <RadioInput
        checked={checkedValue === actionValue}
        label={label}
        onChange={(e) => {
          setReviewActionValue(actionValue);
          setFieldValue("action", actionValue);
        }}
        value={actionValue}
        name="role_id"
        className="theme-check-input"
        themeCheck={true}
        touched={touched?.action! as boolean}
      />
    );
  };

  /**
   * * Handles the download of documents by creating a temporary link and triggering a click event.
   * @param url - The URL of the document to be downloaded.
   */
  const handleDownloadDocument = (url: string | null) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "document.pdf"); // Set the file name for download
      link.target = "_blank"; // Open in a new tab
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error("No URL provided for download.");
    }
  };

  /**
   * Handles the action of making a candidate inactive.
   * Dispatches the `makeCandidateInactive` action with the current candidate ID.
   * If the action is fulfilled, updates the state to reflect the candidate's inactive status
   * and displays a success toast notification.
   *
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when the action is completed.
   */
  const handleMakeInactive = async () => {
    const response = await dispatch(
      makeCandidateInactive({ candidate_id: currentCandidatePrimaryId })
    );
    if (isFulfilled(response)) {
      toast.success("Candidate has been made inactive successfully.");
      setShowModal(false);
      // getAllCandidates?.(); // Optional chaining to safely call if defined
      document.body.classList.remove("modal-open");
    }
  };

  /**
   * Handles the Access Onboarding (Impersonation) feature.
   * Generates an impersonation token and opens the candidate's onboarding screens in a new tab.
   * The admin can then view and edit the candidate's information on their behalf.
   *
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when the new tab is opened.
   */
  const handleAccessOnboarding = async () => {
    if (!currentCandidateUUID) {
      toast.error("Unable to access onboarding: Candidate UUID not found.");
      return;
    }

    setIsAccessingOnboarding(true);
    try {
      await ImpersonationService.accessOnboarding(
        currentCandidateUUID,
        `Accessing onboarding for ${reviewCandidateData?.first_name} ${reviewCandidateData?.last_name}`
      );
     
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to access candidate onboarding. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsAccessingOnboarding(false);
    }
  };

  const handleFileSelect = (file: TFileUploadsResponse, fileName: string) => {
    setFieldValue(file.data.document_type + "_doc", file.data.resume);
    setFieldValue(file.data.document_type + "_doc_name", fileName);
  };

  const validDocumentTypes = [
    "police_check",
    "working_with_children_check",
    "right_to_work",
    "reference_check",
    "reference_check_1",
  ];

  // Stage 2 compliance can no longer be bypassed: every compliance file must be uploaded (or Referoo complete)
  const allDocumentsUploaded = !validDocumentTypes.some((type: string) =>
    !(reviewCandidateData?.documents || []).find(
      (file: TUploadedDocumentsData) => file.document_type === type
    )?.file_name
  );
  const complianceDocsReady = allDocumentsUploaded && expiredComplianceDocs.length === 0;

  const isComplianceCheckErrored = (() => {
    if (expiredComplianceDocs.length > 0) return true;
    const isReferooComplete = referroStatus === "complete" && latestStage2Action === "referoo";

    if(latestStage2Action === "admin-complete" && allDocumentsUploaded) {
      return false;
    } else if(latestStage2Action === "referoo" && isReferooComplete === false) {
      return true;
    } else if(allDocumentsUploaded === false && isReferooComplete === false) {
      return true;
    }
    return false;
  })();

  const isStage3Approved = reviewCandidateData?.profile?.current_stage === 3 && reviewCandidateData?.profile?.review_status === "approved";

  // Button must stay disabled at stage 1 (non-decline) until both ID verification and
  // online interview are either completed (=1) or explicitly bypassed (=1).
  const isStage1NotReady =
    currentStage === 1 &&
    values.action !== "decline" &&
    !(
      (reviewCandidateData?.completion_status?.id_verification === 1 || values?.bypass_id_verification === 1) &&
      (reviewCandidateData?.completion_status?.online_interview === 1 || values?.bypass_online_interview === 1)
    );


  
  useEffect(() => {
    if (currentStage === 2) {
      const lastAction =
        reviewCandidateData?.profile?.profile_steps?.[0]?.action;
      if (lastAction === "approve_candidate_bypass_compliance_check") {
        let notValidDocList = false;
        validDocumentTypes.map((type: string) => {
          const reviewCandidateDocumentList =
            reviewCandidateData?.documents || [];
          const reviewDocument = reviewCandidateDocumentList.find(
            (file: TUploadedDocumentsData) => file.document_type === type
          );
          if (
            reviewDocument?.file_name === "" ||
            reviewDocument?.file_name === undefined
          ) {
            notValidDocList = true;
          }

          // if(notValidDocList && values.action !== "decline" && !values.bypass_compliance_check && !isAdminApproveReferoo) {
          //   setIsDisabled(true);
          // } else if(isAdminApproveReferoo || values.action === "decline") {
          //   setIsDisabled(false);
          // } else if(values.action === "decline") {
          //   setIsDisabled(false);
          // } else if(values.bypass_compliance_check){
          //   setIsDisabled(false);
          // }

          notValidDocList &&
          values.action !== "decline" &&
          reviewCandidateData?.referoo_status !== "complete"
            ? setIsDisabled(true)
            : setIsDisabled(false);
          return null;
        });
      } else if(lastAction === "approve_candidate_to_stage_2") {
        // The admin-complete path (reopened stage 2 with admin uploading docs) is
        // fully handled by the other useEffect that checks latestStage2Action.
        // If we don't bail here, that useEffect's setIsDisabled(true) gets overridden
        // by the else-branch below whenever referoo_status === "complete".
        if (latestStage2Action === "admin-complete") return;

        let notValidDocList = false;
        validDocumentTypes.map((type: string) => {
          const reviewCandidateDocumentList =
            reviewCandidateData?.documents || [];
          const reviewDocument = reviewCandidateDocumentList.find(
            (file: TUploadedDocumentsData) => file.document_type === type
          );

          const checkPCExpired = reviewDocument?.issued_date && isDateExpired(reviewDocument?.issued_date);
          const checkWCCExpired = reviewDocument?.validity_date && isDateExpired(reviewDocument?.validity_date);
          
          if (
            (!reviewDocument?.file_name || reviewDocument?.file_name === "") ||
            (
              reviewDocument?.document_type === "police_check" &&
              reviewDocument?.file_name &&
              !reviewDocument?.issued_date && !checkPCExpired
            ) ||
            (
              reviewDocument?.document_type === "working_with_children_check" &&
              reviewDocument?.file_name &&
              !reviewDocument?.validity_date && !checkWCCExpired
            )
          ) {
            notValidDocList = true;
          }

          notValidDocList &&
          values.action !== "decline" &&
          reviewCandidateData?.referoo_status !== "complete"
            ? setIsDisabled(true)
            : setIsDisabled(false);
          return null;
        });
      }
    }
  }, [
    currentStage,
    reviewCandidateData,
    values.action,
    validDocumentTypes,
  ]);

  // useEffect(() => {
  //   const lastAction = reviewCandidateData?.profile?.profile_steps?.[0]?.action;
  //   if (lastAction === "approve_candidate_bypass_compliance_check") {
  //     if(isAdminApproveReferoo){
  //       setIsDisabled(false);
  //     }else {
  //       setIsDisabled(true);
  //     }
  //   }
  // }, [isAdminApproveReferoo, reviewCandidateData])

  const handleReopenStageConfirm = async (payload: {
    candidate_id: number;
    stage_number: number;
    notify_employee: boolean;
    reopened_sections?: string[];
    reason?: string;
  }) => {
    setReopenStageError(null);
    try {
      await dispatch(reopenStageThunk(payload)).unwrap();

      setShowReopenStageModal(false);
      setReopenStageError(null);

      // Refresh candidate data after reopening
      if (handleReviewCandidate) {
        handleReviewCandidate(
          reviewCandidateData?.id || 0,
          reviewCandidateData?.uuid || '',
          reviewCandidateData?.candidate_primary_key || 0
        );
      }
    } catch (err: any) {
      setReopenStageError(err?.message || 'Failed to reopen stage. Please try again.');
    }
  };

  const handleCloseLoadAllowance = () => {
    setShowAllowanceLoad(false);
    setShowModal(true);
  }

  const handleOpenLoadAllowance = () => {
    setShowAllowanceLoad(true);
    setShowModal(false);
  }

  const handleEditProfile = (candidateId: number) => {
    CandidateStorage.save(candidateId ?? 0); // Save candidate ID to session storage

    navigate(AUTH_ROUTE.ADMIN_CANDIDATE_PROFILE);
  }; 

  const getStageInfo = (stageNumber: number) =>
    getStageInfoHelper(reviewCandidateData, reopenedStagesStatus, stageNumber);



  
  // const reopenedStageNumbers = [1, 2, 3].filter((s) => getStageInfo(s)?.status === 'reopened');
  const reopenedStageNumbers = reviewCandidateData?.profile?.reopened_stages || [];
  const isAnyStageReopened = reopenedStageNumbers?.length === 0 ? false : true;

  const computeMissingDocLabels = (): string[] => {
    const labels: string[] = [];
    if (tfnFormUrl === null) labels.push("TFN Declaration");
    if (superFormUrl === null) labels.push("Superannuation");
    if (contractForm === null) labels.push("Contract");
    if (!reviewCandidateData?.profile?.accept_terms) labels.push("Casual & Fair Work Statement");
    if (stepCompletion?.bankAccount !== 1) labels.push("Bank Account");
    if (stepCompletion?.emergency_info !== 1) labels.push("Next of Kin");
    return labels;
  };

  const complianceDocLabels: Record<string, string> = {
    police_check: "Police Check",
    working_with_children_check: "Working With Children Check",
    right_to_work: "Right To Work",
    reference_check: "Reference Check",
    reference_check_1: "Reference Check",
  };

  const computeMissingComplianceDocs = (): string[] => {
    const docs = reviewCandidateData?.documents || [];
    const hasInDocs = (type: string) => !!docs.find((d) => d.document_type === type)?.file_name;

    const isPresent: Record<string, boolean> = {
      police_check:
        hasInDocs("police_check") ||
        referooPoliceCheckDoc.length > 0 ||
        reviewCandidateData?.profile?.has_police_check === 1,
      working_with_children_check:
        hasInDocs("working_with_children_check") || referooRightToWorkDoc.length > 0,
      right_to_work:
        hasInDocs("right_to_work") || referooRightToWorkMainDoc.length > 0,
      reference_check:
        hasInDocs("reference_check") ||
        hasInDocs("reference_check_1") ||
        referooReferenceCheckDoc.length > 0,
      reference_check_1:
        hasInDocs("reference_check") ||
        hasInDocs("reference_check_1") ||
        referooReferenceCheckDoc.length > 0,
    };

    const missingTypes = validDocumentTypes.filter((type) => !isPresent[type]);
    // Deduplicate labels (reference_check and reference_check_1 share the same label)
    return Array.from(new Set(missingTypes.map((type) => complianceDocLabels[type])));
  };

  const formatDocList = (items: string[]): string => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
  };

  const getActionAlertMessage = (): string | null => {
    const hasReopenedStages = reopenedStageNumbers.length > 0;
    const hasDocIssue = !isValidDocAndContracts && currentStage === 3;
    const hasComplianceIssue = isComplianceCheckErrored && currentStage === 2;

    const missingDocLabels = computeMissingDocLabels();
    const missingDocText = formatDocList(missingDocLabels);

    // Stage 3: reopened stages + doc issue combined
    if (hasReopenedStages && hasDocIssue) {
      const nonStage3 = reopenedStageNumbers.filter((s) => s !== 3);
      if (nonStage3.length > 0) {
        const stageLabels = nonStage3.map((s) => `Stage ${s}`).join(' and ');
        return missingDocText
          ? `Please ensure the employee completes ${stageLabels}, the ${missingDocText} before proceeding.`
          : `Please ensure the employee completes ${stageLabels} before proceeding.`;
      }
      return missingDocText
        ? `Please ensure the employee completes the ${missingDocText} before proceeding.`
        : null;
    }

    // Stage 2: reopened stages + compliance issue combined
    if (hasReopenedStages && hasComplianceIssue) {
      const stageLabels = reopenedStageNumbers.map((s) => `Stage ${s}`).join(' and ');
      const missingComplianceDocs = computeMissingComplianceDocs();
      const complianceDocText = formatDocList(missingComplianceDocs);
      const compliancePart = complianceDocText
        ? `${complianceDocText} will need to be uploaded before proceeding.`
        : 'Compliance documents will need to be uploaded before proceeding.';
      return `Please ensure ${stageLabels} is completed. ${compliancePart}`;
    }

    if (hasReopenedStages) {
      const stageLabels = reopenedStageNumbers.map((s) => `Stage ${s}`).join(' and ');
      return `Please ensure ${stageLabels} ${reopenedStageNumbers.length === 1 ? 'is' : 'are'} completed.`;
    }

    if (hasDocIssue) {
      return missingDocText
        ? `Please ensure the employee completes the ${missingDocText} before proceeding.`
        : null;
    }

    if (hasComplianceIssue) {
      const missingComplianceDocs = computeMissingComplianceDocs();
      const complianceDocText = formatDocList(missingComplianceDocs);
      const compliancePart = complianceDocText
        ? `${complianceDocText} will need to be uploaded before proceeding.`
        : 'Compliance documents will need to be uploaded before proceeding.';
      return `Please ensure Stage 2 is completed. ${compliancePart}`;
    }

    return null;
  };


 
  return ( 
    <>
      {
        <PopupModal
          visible={showModal}
          showCloseBtn={true}
          onClickClose={onClickClose}
          customWidth={1100}
        >
          <div className="d-flex flex-column gap-20">
            <div
              className={`d-flex flex-column flex-lg-row gap-20 align-items-lg-center ${
                currentStage <= 3 ? "" : "d-none"
              }`}
            >
              {reviewCandidateData?.photo === null ||
              reviewCandidateData?.photo?.length === 0 ? (
                <PlaceHolderImage />
              ) : (
                <img
                  src={reviewCandidateData?.photo ?? undefined}
                  alt="Candidate"
                  className="w-130px h-130px rounded-circle"
                />
              )}

              <div className="d-flex flex-column flex-lg-row gap-20 align-items-center flex-grow-1">
                <div className="flex-grow-1 d-flex flex-column gap-10 fw-400 text-black text-break">
                  <div className="fs-30 lh-30px">
                    {toTitleCase(reviewCandidateData?.first_name || "")}{" "}
                    {toTitleCase(reviewCandidateData?.last_name || "")}
                  </div>
                  <div className="d-flex flex-column fs-16">
                    {/* <div>
                      {getCandidateQualification &&
                        getCandidateQualification(
                          reviewCandidateData?.qualifications?.qualification ??
                            "",
                          reviewCandidateData?.qualifications
                            ?.alternative_positions ?? ""
                        )}
                    </div> */}
                    {
                      (reviewCandidateData?.profile.verification_status === "verified" && (reviewCandidateData?.is_documents_expired === 1 || !!reviewCandidateData?.documents_expire_one_month)) ? (
                        <div className="d-flex align-self-start py-4 px-8 bg-white border-1 border-solid border-alert radius-8px text-alert fs-13 fw-400">{ reviewCandidateData?.is_documents_expired === 1 ? `Non-Compliant` : `Compliance Alert` }</div>
                      ) : ""
                    }
                    
                    <div>{reviewCandidateData?.positions}</div>
                    <div>Lives in {extractSuburbFromAddress(reviewCandidateData?.address ?? "")}</div>
                  </div>
                  {/* Render all stages dynamically */}
                  <div className="d-flex flex-column gap-12">
                    {[1, 2, 3].map((stageNum) => {
                      const stageInfo = getStageInfo(stageNum);
                      
                      // Only render stages that have been started
                      if (!stageInfo) return null; 

                      // If stage 1 is still in-progress (not yet submitted), only show stage 1
                      // const stage1Info = getStageInfo(1);
                      // if (stageNum === 1 && stage1Info?.status === 'in-progress') return null;

                      return (
                        <StageDisplay
                          key={stageNum}
                          stageNumber={stageNum}
                          status={stageInfo.status}
                          submittedDate={stageInfo.submittedDate}
                          startedDate={stageInfo.startedDate}
                          reopenedDate={stageInfo.reopenedDate}
                          onReopenStage={() => {
                            setReopenStageNumber(stageNum);
                            setShowReopenStageModal(true);
                          }}
                          showReopenLink={stageInfo.status === 'submitted' || stageInfo.status === 'resubmitted'}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="w-10px"></div>
                { (
                    reviewCandidateData?.profile?.review_status !== null &&
                    reviewCandidateData?.profile?.current_stage !== null &&
                    !(currentStage === 1 && getStageInfo(1)?.status === 'reopened')
                    // (reviewCandidateData && currentStage === 1 && !isStageReopened(reviewCandidateData || null, 1))
                    // && reviewCandidateData?.profile?.interview_complete_later === 1
                  ) ? 
                <div className="min-w-420px w-420px mobile-w-100 bg-teal-pale border border-teal-dark radius-8px p-20px d-flex flex-column gap-10 fw-400 text-black">
                  <div className="fs-16">Select the next step below:</div>

                  {currentStage === 1 ? (
                    <div className="theme-check">
                      {renderRadioInput(
                        values?.action,
                        "Approve employee to move forward to Stage 2",
                        "approve_candidate_to_stage_2",
                        setFieldValue,
                        setReviewActionValue
                      )}
                    </div>
                  ) : (
                    ""
                  )}

                  {currentStage === 2 ? (
                    <div className="theme-check">
                      {renderRadioInput(
                        values?.action,
                        "Approve employee to move forward to Stage 3",
                        "approve_candidate_to_stage_3",
                        setFieldValue,
                        setReviewActionValue
                      )}
                    </div>
                  ) : (
                    ""
                  )}

                  {currentStage === 1 && (
                    <div className="theme-check">
                      {renderRadioInput(
                        values?.action,
                        "Approve employee, but admin completes Stage 2: Compliance Check on employee's behalf",
                        "approve_candidate_bypass_compliance_check",
                        setFieldValue,
                        setReviewActionValue
                      )}
                    </div>
                  )}

                  {currentStage === 3  && (
                    <div className="theme-check">
                      {renderRadioInput(
                        values?.action,
                        "Approve and activate employee profile",
                        "activate",
                        setFieldValue,
                        setReviewActionValue
                      )}
                    </div>
                  )}

                  <div className="theme-check">
                    {renderRadioInput(
                      values?.action,
                      "Decline employee",
                      "decline",
                      setFieldValue,
                      setReviewActionValue
                    )}
                  </div>

                  {!(
                    reviewCandidateData?.completion_status?.id_verification ||
                    reviewCandidateData?.completion_status?.online_interview
                  ) &&
                  (currentStage === null || currentStage === 1) && isDisabled ? (
                    <div className="d-flex py-10px gap-5 align-items-center">
                      <Warning width={"18"} height={"18"} color={"#e52514"} />
                      <div className="fs-13 fw-400 text-alert ">
                        Please review the missing steps. You can bypass the
                        requirements OR request employee to complete.
                      </div>
                    </div>
                  ) : (
                    ""
                  )}

                 
                  {getActionAlertMessage() && values.action !== "decline" && (
                    <ErrorMessage message={getActionAlertMessage()!} />
                  )}
                  <div className="d-flex justify-content-end">
                    <CustomButton
                      type="submit"
                      variant="primary"
                      onClick={handleSubmit}
                      className="btn-theme bg-teal text-black action_next_step"
                      isLoading={inProgress}
                      contentClass="d-flex"
                      disabled={!values.action || isDisabled || (isAnyStageReopened && values.action !== "decline")}
                    >
                      <RoundedTick
                        color={
                          inProgress || !values.action || isDisabled || (isAnyStageReopened && values.action !== "decline")
                            ? "#aaaaaa"
                            : "black"
                        }
                      />
                      {currentStage === 3 ? "Activate Profile" : "Action Next Step"}
                    </CustomButton>
                  </div>
                </div> : "" }
                <div className="w-20px"></div>
              </div>
            </div>
            {/* 3.5 Candidate Management - Candidate Profile */}
            <div
              className={`d-flex flex-column flex-lg-row gap-20 align-items-lg-center ${
                currentStage > 3 ? "" : "d-none"
              }`}
            >
              {reviewCandidateData?.photo === null ||
              reviewCandidateData?.photo?.length === 0 ? (
                <PlaceHolderImage />
              ) : (
                <img
                  src={reviewCandidateData?.photo ?? undefined}
                  alt="Candidate"
                  className="w-130px h-130px rounded-circle"
                />
              )}
              <div className="d-flex flex-column flex-lg-row gap-60 gap-mobile-20 align-items-lg-center flex-grow-1">
                <div className="d-flex flex-column gap-15 flex-grow-1 text-break">
                  <div className="fs-30 fw-400 text-black">
                    {toTitleCase(reviewCandidateData?.first_name || "")}{" "}
                    {toTitleCase(reviewCandidateData?.last_name || "")}
                  </div>
                  <StarRating rating={reviewCandidateData?.rating?.average_rating ? reviewCandidateData?.rating?.average_rating : 0} />
                  <div className="d-flex flex-column fs-16 fw-400 text-black">
                    <div>
                      {/* {convertToTitleCase(
                        reviewCandidateData?.qualifications?.qualification
                      )} */}
                      {reviewCandidateData?.positions}
                    </div>
                    
                    <div>Lives in {extractSuburbFromAddress(reviewCandidateData?.address ?? "")}</div>
                  </div>   
                  {
                    (reviewCandidateData?.profile.verification_status === "verified" && (reviewCandidateData?.is_documents_expired === 1 || !!reviewCandidateData?.documents_expire_one_month)) ? (
                      <div className="d-flex align-self-start py-4 px-8 bg-white border-1 border-solid border-alert radius-8px text-alert fs-13 fw-400">{ reviewCandidateData?.is_documents_expired === 1 ? `Non-Compliant` : `Compliance Alert` }</div>
                    ) : ""
                  }
                  <div className="d-flex gap-10 align-items-center">                    
                      {
                        reviewCandidateData && reviewCandidateData?.profile?.loading_amount > 0 && (
                          <div className="bg-teal-muted border border-black radius-8px">
                            <div className="fs-13 fw-400 text-black lh-24px px-5px">
                              {(reviewCandidateData?.profile?.loading_amount ?? 0) >= 0 && "+"}{(reviewCandidateData?.profile?.loading_amount ?? 0) > 0 ? formatAllowance(reviewCandidateData?.profile?.loading_amount ?? 0) : ""}
                            </div>
                          </div>
                        )
                      }
                    <div className="fs-13 fw-400 text-grey-medium">
                      Loading {reviewCandidateData?.profile?.loading_amount_added_on === null ? "not" : ""} added {formatDate(reviewCandidateData?.profile?.loading_amount_added_on ? new Date(reviewCandidateData.profile.loading_amount_added_on) : null)}
                    </div>
                    <div onClick={() => handleOpenLoadAllowance()} className="cursor-pointer">
                      <EditPencil color="#AAAAAA" size={20} />
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-column gap-30 gap-mobile-20 min-w-486px mobile-w-100">
                  <div className="d-flex gap-40 gap-mobile-20 flex-wrap">
                    <div className="d-flex flex-column gap-3">
                      <div className="fs-10 fw-400 text-grey-medium">
                        Past Jobs at <br /> Any Centre
                      </div>
                      <div className="fs-13 text-black">
                        {reviewCandidateData?.past_jobs_count_without_centre || 0} { (reviewCandidateData?.past_jobs_count_without_centre && reviewCandidateData?.past_jobs_count_without_centre > 1) ? "Jobs" : "Job" }
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      <div className="fs-10 fw-400 text-grey-medium">
                        Last Job at Any Centre
                      </div>
                      <div className="d-flex flex-column">
                        {reviewCandidateData?.last_job_date ? (
                          <div className="fs-13 text-black">
                            {reviewCandidateData?.last_job_date}
                          </div>
                        ) : (
                          <div className="fs-13 text-black"> {"-"}</div>
                        )}
                        <div className="fs-13 text-grey-medium">
                          { reviewCandidateData?.last_job_centre && `(${reviewCandidateData?.last_job_centre})`}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      <div className="fs-10 fw-400 text-grey-medium">
                        Marked as Preferred at
                      </div>
                      <div className="fs-13 text-black">
                        {reviewCandidateData?.preferred_centres_count ?? 0}{" "}
                        { (reviewCandidateData?.preferred_centres_count && reviewCandidateData?.preferred_centres_count > 1) ? "Centres" : "Centre" }{" "}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-40 gap-mobile-20 flex-wrap fs-13 text-black">
                    <div className={`cursor-pointer ${
                        (showCandidateJobs && typeofCandidateJobs === "declined") ? "fw-700" : "text-decoration-underline"
                      }`} onClick={() => {
                        setShowCandidateJobs(true)
                        setShowAdminComments(false)
                        setTypeofCandidateJobs("declined")
                        } }>
                      {reviewCandidateData?.decline_jobs_count} Job{(reviewCandidateData && reviewCandidateData?.decline_jobs_count > 1) ? "s" : ""} Declined
                    </div>

                    <div className={`cursor-pointer ${
                        (showCandidateJobs && typeofCandidateJobs === "withdrawn") ? "fw-700" : "text-decoration-underline"
                      }`} onClick={() => {
                        setShowCandidateJobs(true)
                        setShowAdminComments(false)
                        setTypeofCandidateJobs("withdrawn")
                        } }>
                      {reviewCandidateData?.withdrawn_jobs_count} Job{(reviewCandidateData && reviewCandidateData?.withdrawn_jobs_count > 1) ? "s" : ""} Withdrawn
                    </div>

                    <div
                      className={`cursor-pointer ${
                        showAdminComments ? "fw-700" : "text-decoration-underline"
                      }`}
                      onClick={() => { 
                        setShowAdminComments(true)
                        setShowCandidateJobs(false)
                      }}
                    >
                      {totalAdminComments} Admin Comment{(totalAdminComments > 1) ? "s" : ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/** Listing all the make a request from candidates */}
            {reviewCandidateData &&
              reviewCandidateData?.profileEditRequests?.length > 0 && (
                <ProfileEditRequest
                  requestList={reviewCandidateData?.profileEditRequests}
                  candidateId={currentCandidateId}
                  reviewCandidateData={reviewCandidateData}
                  setShowModal={setShowModal}
                  getAllCandidates={getAllCandidates}
                />
              )}

            {/* 3.5 Candidate Management - Candidate Profile */}

            <div
              className={`w-100 h-1px bg-grey-light ${
                (showAdminComments === false && showCandidateJobs === false) ? "" : "d-none"
              }`}
            ></div>
            <div
              className={`d-flex flex-column flex-lg-row gap-50 gap-mobile-20 ${
                (showAdminComments === false && showCandidateJobs === false) ? "" : "d-none"
              }`}
            >
              <div className="min-w-200px d-flex flex-column gap-10">
                <CustomButton
                    variant={"primary"}
                    className="btn-theme bg-teal text-black text-nowrap"
                    onClick={handleAccessOnboarding}
                    isLoading={isAccessingOnboarding}
                    contentClass="d-flex gap-8 align-items-center"
                    disabled={isAccessingOnboarding || !currentCandidateUUID}
                  >
                  <ExternalLink color={isAccessingOnboarding ? "#aaaaaa" : "black"} />  {isStage3Approved ? "Impersonate Mode" : "Access Onboarding"} 
                </CustomButton>

               {reviewCandidateData?.profile?.review_status !== null && ( 
                <CustomButton
                    variant={"primary"}
                    className="btn-theme bg-teal text-black text-nowrap"
                    onClick={() => handleEditProfile(currentCandidateId)}
                    isLoading={isAccessingOnboarding}
                    contentClass="d-flex gap-8 align-items-center"
                    disabled={isAccessingOnboarding || !currentCandidateUUID}
                  >
                  <EditPencil />  { "Edit Profile"} 
                </CustomButton>
              )}

                <div className="d-flex flex-column gap-20">
                  {generateSteps(currentStage)}
                </div>
                {reviewCandidateData?.profile?.verification_status ===
                "verified" ? (
                  <CustomButton
                    variant={"primary"}
                    className="btn-theme bg-off-white text-black"
                    onClick={handleMakeInactive}
                    isLoading={inProgress}
                    contentClass="d-flex"
                  >
                    <InactiveOutline /> Make Inactive
                  </CustomButton>
                ) : null}
              </div>
              <div className="w-100 h-1px bg-off-white d-block d-lg-none"></div>
              <div className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0">
                {/* Personal Details */}
                <div className="d-flex flex-column gap-30">
                  {/* ID Verification */}
                  <IdVerification
                    showIdVerification={showIdVerification}
                    reviewCandidateData={reviewCandidateData}
                    currentStage={currentStage}
                    skipIdVerification={skipIdVerification}
                    setSkipIdVerification={setSkipIdVerification}
                    setFieldValue={setFieldValue}
                    values={values}
                    errors={errors}
                    handleSectionNavigation={handleSectionNavigation}
                  />
                  {/** Personal Details */}
                  <PersonalDetails
                    handleDownloadDocument={handleDownloadDocument}
                    showPersonalDetails={showPersonalDetails}
                    reviewCandidateData={reviewCandidateData}
                  />
                  {/** Qualification */}
                  <Qualification
                    showQualifications={showQualifications}
                    reviewCandidateData={reviewCandidateData}
                    convertToTitleCase={convertToTitleCase}
                    handleFileSelect={handleFileSelect}
                  />
                  {/* Job Preference */}
                  <JobPreference
                    showJobPreference={showJobPreference}
                    reviewCandidateData={reviewCandidateData}
                  />
                  {/* Online Interview */}
                  <OnlineInterview
                    showOnlineInterview={showOnlineInterview}
                    skipOnlineInterview={skipOnlineInterview}
                    reviewCandidateData={reviewCandidateData}
                    setSkipOnlineInterview={setSkipOnlineInterview}
                    setFieldValue={setFieldValue}
                    currentStage={currentStage}
                    values={values}
                    errors={errors}
                  />
                  {/* Compliance Check */}
                  <ComplianceCheck
                    setIsDisabled={setIsDisabled}
                    handleFileSelect={handleFileSelect}
                    showComplianceCheck={showComplianceCheck}
                    skipComplianceCheck={skipComplianceCheck}
                    reviewCandidateData={reviewCandidateData}
                    setSkipComplianceCheck={setSkipComplianceCheck}
                    setFieldValue={setFieldValue}
                    currentStage={currentStage}
                    values={values}
                    reviewActionValue={reviewActionValue}
                    setIsAdminApproveReferoo={setIsAdminApproveReferoo}
                    errors={errors}
                  />
                  {/*  Document & Contracts*/}
                  <DocumentAndContract
                    showDocuments={showDocuments}
                    tfnFormUrl={tfnFormUrl}
                    superFormUrl={superFormUrl}
                    contractForm={contractForm}
                    reviewCandidateData={reviewCandidateData}
                    handleDownloadDocument={handleDownloadDocument}
                  />
                  {/* Emergency info */}
                  <NextOfKin
                    showEmergencyInfo={showEmergencyInfo}
                    reviewCandidateData={reviewCandidateData}
                  />

                  {/* Documents & Contract */}
                </div>
              </div>
            </div>

            {/* 3.6 Candidate Management - Candidate Profile */}
            {showAdminComments && (
              <AdminComments
                candidateId={currentCandidateId}
                setShowAdminComments={setShowAdminComments}
                setTotalAdminComments={setTotalAdminComments}
                totalAdminComments={totalAdminComments}
              />
            )}

            {
              showCandidateJobs && (
              <CandidateJobs
                reviewCandidateData={reviewCandidateData}
                setShowCandidateJobs={setShowCandidateJobs}
                setShowAdminComments={setShowAdminComments}
                typeofCandidateJobs={typeofCandidateJobs}
              />
              )
            }
            {/* 3.6 Candidate Management - Candidate Profile */}
          </div>
        </PopupModal>
      }

      {
        <LoadAllowance
          showAllowanceLoad = {showAllowanceLoad}
          onClickClose={() => handleCloseLoadAllowance()}
          reviewCandidateData={reviewCandidateData}
          convertToTitleCase={convertToTitleCase}
          setShowAllowanceLoad={setShowAllowanceLoad}
          setShowModal={setShowModal}
          />
      }

      {
        <ReopenStageModal
          visible={showReopenStageModal}
          onClose={() => { setShowReopenStageModal(false); setReopenStageError(null); }}
          onConfirm={handleReopenStageConfirm}
          stageNumber={reopenStageNumber}
          candidateId={reviewCandidateData?.id}
          candidateName={`${toTitleCase(reviewCandidateData?.first_name || "")} ${toTitleCase(reviewCandidateData?.last_name || "")}`}
          candidatePosition={reviewCandidateData?.positions}
          candidateAvatar={reviewCandidateData?.photo || undefined}
          error={reopenStageError}
        />
      }
    </>
  );
};

export default CandidateReviewModal;